import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DeviceResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { AveragingStateValues } from 'lavva.exalushome/build/js/Services/StatesHistory/StatesHistory';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ControlWrapper, TimeRanges } from '../../../../../../components';
import { RangeHeader } from '../../../../../../components/range-header';
import { useInstallationContext } from '../../../../../../context/installation';
import { useChartData } from '../../../../hooks/charts';
import { ChartEmptyContainer } from '../components/chart-empty-container/chart-empty-container';
import ChartSummary from '../components/chart-summary';
import CustomizedXTick from '../components/customized-x-tick';
import { ExportButton } from '../components/export-button/export-button';
import LegendCustom from '../components/legend';
import { SensorChartTooltip } from '../components/tooltip';
import { useExportData } from '../hooks/use-export-data';
import { useNavigateCharts } from '../hooks/use-history-charts';

type PressureChartProps = {
  channel: IDeviceChannel;
  active?: boolean;
};

const PressureChart: React.FC<PressureChartProps> = ({ channel, active }) => {
  const { t } = useTranslation('channel-details');
  const { selectedInstallation } = useInstallationContext();
  const { exportPressureData } = useExportData(channel);
  const { activeRange, page, currentRangeLabel, dateFromRange, onClickActiveMeasurementRange, setPage } =
    useNavigateCharts();
  const { data: chartData, isLoading } = useChartData<AveragingStateValues>(
    channel,
    DeviceResponseType.PressureSensorState,
    activeRange,
    dateFromRange,
  );

  const data = useMemo(
    () =>
      chartData?.Data.map((x) => ({
        timestamp: new Date(x.Time).getTime(),
        [t('exalus.pressure.title')]: x.Values?.Average,
        unit: 'hPa',
      })),
    [chartData?.Data],
  );

  const aggregatedData = useMemo(
    () => chartData?.AggregateDataList.find((x) => x.DataEntryIdentity === 'IPressureSensor')?.AggregateData,
    [chartData?.AggregateDataList],
  );

  return (
    <>
      <ControlWrapper className={classNames('control-wrapper--full-space no-center', { active: active })}>
        <div className="exalus-chart-container">
          <TimeRanges
            activeMeasurementRange={activeRange}
            setActiveMeasurementRange={onClickActiveMeasurementRange}
            disableYear
          />
          <RangeHeader prevAvailable={true} nextAvailable={page < 0} range={currentRangeLabel} setPage={setPage} />
          {data && data.length > 0 ? (
            <>
              <div className="binary-chart">
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    width={500}
                    height={250}
                    data={data}
                    margin={{
                      top: 15,
                      right: 20,
                      left: 20,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      tick={<CustomizedXTick />}
                      height={44}
                      scale="time"
                      allowDuplicatedCategory={!false}
                    />
                    <YAxis dataKey={t('exalus.pressure.title')} unit=" hPa" domain={['dataMin', 'dataMax + 0.5']} />
                    <Tooltip content={<SensorChartTooltip />} />
                    <Legend wrapperStyle={{ bottom: -20 }} content={<LegendCustom />} />
                    <Area
                      type="monotone"
                      dataKey={t('exalus.pressure.title')}
                      stroke={selectedInstallation?.hexColor}
                      fill={selectedInstallation?.hexColor}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <ExportButton
                disabled={!chartData || !chartData.Data.length}
                handleExport={() => exportPressureData(chartData)}
              />
            </>
          ) : (
            <ChartEmptyContainer isLoading={isLoading} />
          )}
        </div>
      </ControlWrapper>
      {aggregatedData && <ChartSummary activeRange={activeRange} aggregateData={aggregatedData} unit="hPa" />}
    </>
  );
};

export default PressureChart;
